import { useState } from "react";
import { mutate, reprocessPath, useApi } from "../api/client";
import type { Session } from "../api/auth";

/**
 * Re-run this document's extraction.
 *
 * Lives in the page header rather than inside the History tab: a reader decides
 * to re-run a document while looking at the extraction that disappointed them,
 * and making them find a tab first put the action three clicks from the reason
 * for taking it.
 *
 * Admin-only, because each run is a fresh OCR pass and a model call, billed.
 */
export function RerunButton({
  id,
  onQueued,
}: {
  id: string;
  onQueued?: () => void;
}) {
  const { data: session } = useApi<Session>("/api/orchestrator/auth/session");
  const isAdmin = session?.role === "admin";

  const [running, setRunning] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  const run = async () => {
    setRunning(true);
    setNote(null);
    setFailed(false);
    try {
      await mutate(reprocessPath(id), "POST", {});
      setNote("Queued — extraction takes up to a minute. See the History tab.");
      onQueued?.();
    } catch (e) {
      setFailed(true);
      setNote(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="rerun">
      <button className="btn" onClick={run} disabled={running}>
        {running ? "Queueing…" : "Re-run extraction"}
      </button>
      {note ? (
        <span className={`rerun-note${failed ? " bad" : ""}`}>{note}</span>
      ) : (
        <span className="rerun-note muted">
          Keeps the previous attempt for comparison · spends tokens
        </span>
      )}
    </div>
  );
}
